import { ElementLayout } from "schemas/elements";
import {
	SelectElementDefinition,
	RadioElementDefinition,
	LookupElementDefinition,
	ReferenceElementDefinition,
	PanelElementDefinition,
	DateElementDefinition,
	CheckboxElementDefinition,
	TextInputElementDefinition,
	TextAreaElementDefinition,
	LegacyArrayViewElementDefinition,
	MultiRefViewElementDefinition,
	NestedViewElementDefinition,
	HeadingElementDefinition,
	ParagraphElementDefinition,
	RootViewElementDefinition,
	MediaElementDefinition,
	FormInstanceDataItem,
	FormDataValue,
	FormElements,
} from "../lib/api/types";

export type ElementStatus = "READY" | "LOADING" | "UPDATING" | "ERROR";

export type ElementData<V = FormDataValue> = FormInstanceDataItem & {
	_deleted?: boolean;
	id: string;
	value: V;
};

type FormElementBase<T> = T & {
	_ui: { readonly init: boolean; readonly elemStatus: ElementStatus };
	vid: string;
	elementLayout?: ElementLayout;
};

type ViewConfig<T> = Omit<T, "elements"> & {
	traversal: string[];
	vid: string;
};

//
export type BoundElement = { id: string };

// Specific types
export type SelectElement = FormElementBase<SelectElementDefinition>;
export type RadioElement = FormElementBase<RadioElementDefinition>;
export type LookupElement = FormElementBase<LookupElementDefinition>;
export type ReferenceElement = FormElementBase<ReferenceElementDefinition>;
export type HeadingElement = FormElementBase<HeadingElementDefinition>;
export type ParagraphElement = FormElementBase<ParagraphElementDefinition>;
export type MediaElement = FormElementBase<MediaElementDefinition>;

type PanelConfig = Omit<PanelElementDefinition, "elements"> & {
	traversal: string[];
	label: string;
	elements: FormElements;
};

export type PanelElement = FormElementBase<PanelConfig>;
export type DateElement = FormElementBase<DateElementDefinition>;
export type CheckboxElement = FormElementBase<CheckboxElementDefinition>;
export type TextInputElement = FormElementBase<TextInputElementDefinition>;
export type TextAreaElement = FormElementBase<TextAreaElementDefinition>;

type RootViewConfig = ViewConfig<RootViewElementDefinition>;
export type RootViewElement = FormElementBase<RootViewConfig> & {
	elements: FormElements;
};

type LegacyArrayViewConfig = ViewConfig<LegacyArrayViewElementDefinition>;
export type LegacyArrayViewElement = FormElementBase<LegacyArrayViewConfig>;

type MultiRefViewConfig = ViewConfig<MultiRefViewElementDefinition>;
export type MultiRefViewElement = FormElementBase<MultiRefViewConfig>;

type NestedViewConfig = ViewConfig<NestedViewElementDefinition>;
export type NestedViewElement = FormElementBase<NestedViewConfig>;

export type FormElement =
	| ParagraphElement
	| HeadingElement
	| PanelElement
	| MultiRefViewElement
	| RootViewElement
	| NestedViewElement
	| SelectElement
	| RadioElement
	| LookupElement
	| ReferenceElement
	| DateElement
	| CheckboxElement
	| TextInputElement
	| LegacyArrayViewElement
	| MediaElement
	| TextAreaElement;

// Factory func
export const createElement = <T>(config: T) => {
	return {
		_ui: { init: true, elemStatus: "READY" as ElementStatus },
		...config,
	};
};
